/**
 * Enhanced Pack Bundle Creator - Production Ready v2.1.0
 * Fixed: Complete elimination of dataset API usage causing property name conflicts
 */

class PackBundleCreator {
	static VERSION = '2.1.0';
	static NAMESPACE = 'pbc'; // Short namespace to avoid conflicts

	constructor(config = {}) {
		this.initializeConfig(config);
		this.initializeState();
		this.bindMethods();
		
		console.log(`🎁 Pack Bundle Creator v${PackBundleCreator.VERSION} initialized`);
		
		this.init();
	}

	initializeConfig(userConfig = {}) {
		this.config = {
			api: {
				endpoints: {
					products: "/products.json?limit=10000&include=variants,options,collections",
					addToCart: "/cart/add.js",
					checkout: "/checkout",
					product: "/products"
				},
				retries: {
					maxRetries: 3,
					retryDelay: 1000,
					timeoutMs: 10000
				}
			},
			packs: {
				availableSizes: [3, 4, 5, 7, 9],
				pricing: {
					3: 1051.57,
					4: 1395.88,
					5: 1683.2,
					7: 2205.82,
					9: 2735.52
				},
				currency: 'Rs.',
				defaultSize: 3
			},
			filters: {
				requiredTags: ['Plain', 'T-SHIRT', 'Regular'],
				requiredProductType: 't-shirt',
				requiredOptions: ['Size', 'Color'],
				availableSizes: ['S', 'M', 'L', 'XL', 'XXL']
			},
			ui: {
				debounceDelay: 150,
				cacheExpiry: 5 * 60 * 1000,
				selectors: {
					variantSelection: '.f8pr-variant-selection',
					priceElements: ['p.f8pr-price', '.f8pr-price', '.price', '[data-price]', '.product-price', '.current-price'],
					addToCartBtn: 'form[action="/cart/add"] button, .add-to-cart',
					buyNowBtn: 'shopify-payment-button button, .buy-now, div[class="overlay-dynamic_buy_button inv-btn"]'
				},
				animations: {
					enabled: true,
					duration: 300
				}
			},
			dev: {
				debug: false,
				logLevel: 'info',
				errorTracking: true
			},
			...userConfig
		};
	}

	initializeState() {
		this.state = {
			isLoading: false,
			selectedItems: new Map(),
			products: [],
			validationErrors: [],
			validationWarnings: [],
			initialized: false
		};

		this.runtime = {
			cache: new Map(),
			currentPackConfig: null,
			selectors: [],
			colorImages: {},
			selectedPackSize: null,
			priceUpdateTimer: null,
			observers: new Set(),
			eventListeners: new Set(),
			initializedElements: new WeakSet() // Use WeakSet instead of data attributes
		};

		this.metrics = {
			initTime: performance.now(),
			apiCalls: 0,
			cacheHits: 0,
			cacheMisses: 0
		};
	}

	bindMethods() {
		const methodsToBind = [
			'handleSelectorChange',
			'handleAddToCart', 
			'handleBuyNow',
			'handlePackSizeChange',
			'handleRouteChange',
			'debounceRouteChange'
		];

		methodsToBind.forEach(method => {
			if (typeof this[method] === 'function') {
				this[method] = this[method].bind(this);
			}
		});

		this.debounceRouteChange = this.debounce(this.handleRouteChange, this.config.ui.debounceDelay);
	}

	log(level, message, ...args) {
		const levels = ['debug', 'info', 'warn', 'error'];
		const configLevel = levels.indexOf(this.config.dev.logLevel);
		const messageLevel = levels.indexOf(level);

		if (messageLevel >= configLevel) {
			const prefix = `[${PackBundleCreator.NAMESPACE}:${level.toUpperCase()}]`;
			console[level](prefix, message, ...args);
		}
	}

	handleError(context, error, options = {}) {
		this.log('error', `Error in ${context}:`, error);

		if (this.config.dev.errorTracking) {
			this.trackError(context, error, options);
		}

		if (options.showUser) {
			this.showUserError(options.userMessage || 'An error occurred. Please try again.');
		}

		if (options.fallback && typeof options.fallback === 'function') {
			try {
				options.fallback();
			} catch (fallbackError) {
				this.log('error', 'Fallback function failed:', fallbackError);
			}
		}
	}

	trackError(context, error, metadata) {
		const errorData = {
			timestamp: new Date().toISOString(),
			context,
			message: error.message,
			stack: error.stack,
			userAgent: navigator.userAgent,
			url: window.location.href,
			metadata
		};

		this.log('debug', 'Error tracked:', errorData);
	}

	showUserError(message) {
		const existingError = document.querySelector(`.${PackBundleCreator.NAMESPACE}-error`);
		if (existingError) existingError.remove();

		const errorDiv = this.createElement('div', {
			className: `${PackBundleCreator.NAMESPACE}-error`
		});

		errorDiv.style.cssText = `
			background: #fef2f2;
			border: 1px solid #fecaca;
			color: #991b1b;
			padding: 12px;
			border-radius: 6px;
			margin: 10px 0;
			font-size: 14px;
			position: relative;
			z-index: 1000;
		`;

		errorDiv.textContent = message;

		const bundleWrapper = document.querySelector(`.${PackBundleCreator.NAMESPACE}`);
		if (bundleWrapper) {
			bundleWrapper.appendChild(errorDiv);
		} else {
			document.body.appendChild(errorDiv);
		}

		setTimeout(() => errorDiv.remove(), 10000);
	}

	init() {
		if (this.state.initialized) {
			this.log('warn', 'Already initialized');
			return;
		}

		try {
			this.log('info', 'Initializing Pack Bundle Creator...');
			this.setupObserver();
			this.state.initialized = true;
			this.handleRouteChange();
			
			this.log('info', 'Initialization complete');
		} catch (error) {
			this.handleError('init', error, {
				showUser: true,
				userMessage: 'Failed to initialize pack bundle creator'
			});
		}
	}

	waitForElement(selector, timeout = 5000) {
		return new Promise((resolve, reject) => {
			const existingElement = document.querySelector(selector);
			if (existingElement) {
				resolve(existingElement);
				return;
			}

			const observer = new MutationObserver((mutations, obs) => {
				const element = document.querySelector(selector);
				if (element) {
					obs.disconnect();
					resolve(element);
				}
			});

			observer.observe(document.body, { 
				childList: true, 
				subtree: true 
			});

			const timeoutId = setTimeout(() => {
				observer.disconnect();
				reject(new Error(`Element not found: ${selector}`));
			}, timeout);

			this.runtime.observers.add({ observer, timeoutId });
		});
	}

	setupObserver() {
		const domObserver = new MutationObserver(() => {
			this.debounceRouteChange();
		});

		domObserver.observe(document.body, { 
			childList: true, 
			subtree: true 
		});

		let lastUrl = window.location.href;
		
		const checkUrlChange = () => {
			const currentUrl = window.location.href;
			if (currentUrl !== lastUrl) {
				lastUrl = currentUrl;
				this.handleRouteChange();
			}
		};

		const urlCheckInterval = setInterval(checkUrlChange, 100);
		
		window.addEventListener('popstate', checkUrlChange);

		const originalPushState = history.pushState;
		const originalReplaceState = history.replaceState;

		history.pushState = (...args) => {
			originalPushState.apply(history, args);
			setTimeout(checkUrlChange, 0);
		};

		history.replaceState = (...args) => {
			originalReplaceState.apply(history, args);
			setTimeout(checkUrlChange, 0);
		};

		this.runtime.observers.add({
			domObserver,
			urlCheckInterval,
			cleanup: () => {
				domObserver.disconnect();
				clearInterval(urlCheckInterval);
				history.pushState = originalPushState;
				history.replaceState = originalReplaceState;
				window.removeEventListener('popstate', checkUrlChange);
			}
		});
	}

	async handleRouteChange() {
		try {
			const pathname = window.location.pathname;
			
			if (!pathname.startsWith('/products/')) {
				this.cleanup();
				return;
			}

			const packConfig = this.parsePackProduct(pathname);

			if (!packConfig.isPackProduct) {
				this.restoreDefaultBehavior();
				return;
			}

			if (this.runtime.currentPackConfig && 
				this.runtime.currentPackConfig.productSlug === packConfig.productSlug &&
				this.runtime.currentPackConfig.packCount === packConfig.packCount) {
				return;
			}

			await this.setupPackBundle(packConfig);

		} catch (error) {
			this.handleError('handleRouteChange', error, {
				showUser: true,
				fallback: () => this.restoreDefaultBehavior()
			});
		}
	}

	parsePackProduct(pathname) {
		const patterns = [
			/\/products\/pick-any-(\d+)/i,
			/\/products\/.*pack.*(\d+)/i,
			/\/products\/.*bundle.*(\d+)/i
		];

		let packCount = 0;
		let isPackProduct = false;

		for (const pattern of patterns) {
			const match = pathname.match(pattern);
			if (match) {
				packCount = parseInt(match[1], 10);
				isPackProduct = true;
				break;
			}
		}

		if (!isPackProduct && pathname.includes('pick-any')) {
			isPackProduct = true;
			packCount = this.config.packs.defaultSize;
		}

		if (packCount > 0 && !this.config.packs.availableSizes.includes(packCount)) {
			this.log('warn', `Invalid pack count ${packCount}, using default`);
			packCount = this.config.packs.defaultSize;
		}

		return {
			isPackProduct,
			packCount,
			productSlug: pathname.replace('/products/', '')
		};
	}

	schedulePriceUpdate(delay = 100) {
		if (this.runtime.priceUpdateTimer) {
			clearTimeout(this.runtime.priceUpdateTimer);
		}
		
		this.runtime.priceUpdateTimer = setTimeout(() => {
			this.runtime.priceUpdateTimer = null;
			this.updatePrice();
		}, delay);
	}

	async setupPackBundle(packConfig) {
		try {
			this.log('debug', 'Starting setupPackBundle with config:', packConfig);
			
			this.resetState();
			this.runtime.selectedPackSize = packConfig.packCount || this.config.packs.defaultSize;

			const sizeVariantSelector = await this.waitForElement(
				this.config.ui.selectors.variantSelection,
				5000
			);

			// Use WeakSet instead of data attributes to track initialization
			if (this.runtime.initializedElements.has(sizeVariantSelector)) {
				this.log('debug', 'Element already initialized');
				return;
			}

			this.runtime.initializedElements.add(sizeVariantSelector);
			this.runtime.currentPackConfig = packConfig;

			this.log('debug', 'Proceeding with pack bundle setup');

			sizeVariantSelector.classList.add('hidden');
			this.showLoadingState(sizeVariantSelector);
			this.setState({ isLoading: true });

			const products = await this.getProductsWithCache();
			const packProducts = this.filterPackProducts(products);

			if (!packProducts.length) {
				throw new Error('No eligible products found for pack bundle');
			}

			this.setState({ 
				products: packProducts, 
				isLoading: false 
			});

			this.removeLoadingState();
			this.createBundleInterface(sizeVariantSelector, packProducts, packConfig);
			this.setupButtonOverrides();
			this.initializeSelectorsState(this.runtime.selectedPackSize);

			setTimeout(() => {
				const priceUpdated = this.updatePrice();
				if (!priceUpdated) {
					setTimeout(() => this.updatePrice(), 500);
				}
			}, 100);

			this.log('info', `Pack bundle setup complete for ${this.runtime.selectedPackSize} items`);

		} catch (error) {
			this.handleError('setupPackBundle', error, {
				showUser: true,
				fallback: () => this.restoreDefaultBehavior()
			});
		}
	}

	filterPackProducts(data) {
		const validProducts = data.products.filter(product => {
			try {
				const hasRequiredTags = this.config.filters.requiredTags.every(tag =>
					product.tags.some(productTag => 
						productTag.toLowerCase().includes(tag.toLowerCase())
					)
				);

				const hasCorrectType = product.product_type && 
					product.product_type.toLowerCase().includes(this.config.filters.requiredProductType);

				const hasRequiredOptions = this.config.filters.requiredOptions.every(option =>
					product.options && product.options.some(productOption => 
						productOption.name === option
					)
				);

				const hasAvailableVariants = product.variants && 
					product.variants.some(variant => variant.available);

				return hasRequiredTags && hasCorrectType && hasRequiredOptions && hasAvailableVariants;

			} catch (error) {
				this.log('warn', 'Error filtering product:', product.handle, error);
				return false;
			}
		});

		this.log('info', `Filtered ${validProducts.length} eligible products from ${data.products.length} total`);
		return validProducts;
	}

	extractUniqueOptions(products) {
		const sizesSet = new Set();
		const colorsSet = new Set();
		const colorImages = {};

		try {
			products.forEach(product => {
				if (!product.variants) return;

				product.variants.forEach(variant => {
					if (!variant.available) return;

					if (variant.option1) {
						const size = variant.option1.trim();
						if (this.isValidSize(size)) {
							sizesSet.add(size);
						}
					}

					if (variant.option2) {
						const color = variant.option2.trim();
						colorsSet.add(color);

						if (!colorImages[color]) {
							if (variant.featured_image?.src) {
								colorImages[color] = variant.featured_image.src;
							} else if (product.images?.length > 0) {
								const imageUrl = typeof product.images[0] === 'string' 
									? product.images[0] 
									: product.images[0].src;
								colorImages[color] = imageUrl;
							}
						}
					}
				});
			});

			let finalSizes = Array.from(sizesSet);
			if (finalSizes.length === 0) {
				this.log('warn', 'No sizes found in products, using configured defaults');
				finalSizes = [...this.config.filters.availableSizes];
			}

			finalSizes = this.sortSizes(finalSizes);
			this.runtime.colorImages = colorImages;

			this.log('debug', 'Extracted options:', {
				sizes: finalSizes,
				colors: Array.from(colorsSet),
				colorImages: Object.keys(colorImages).length
			});

			return {
				sizes: finalSizes,
				colors: Array.from(colorsSet),
				colorImages
			};

		} catch (error) {
			this.handleError('extractUniqueOptions', error);
			
			return {
				sizes: [...this.config.filters.availableSizes],
				colors: [],
				colorImages: {}
			};
		}
	}

	isValidSize(size) {
		const normalizedSize = size.toUpperCase();
		const validSizes = this.config.filters.availableSizes.map(s => s.toUpperCase());
		return validSizes.includes(normalizedSize);
	}

	sortSizes(sizes) {
		const sizeOrder = {
			'XS': 1, 'S': 2, 'M': 3, 'L': 4, 'XL': 5, 'XXL': 6, '2XL': 6, '3XL': 7
		};

		return sizes.sort((a, b) => {
			const orderA = sizeOrder[a.toUpperCase()] || 999;
			const orderB = sizeOrder[b.toUpperCase()] || 999;
			return orderA - orderB;
		});
	}

	async getProductsWithCache() {
		const cacheKey = 'products_data';
		const cached = this.runtime.cache.get(cacheKey);
		
		if (cached && Date.now() - cached.timestamp < this.config.ui.cacheExpiry) {
			this.metrics.cacheHits++;
			this.log('debug', 'Using cached products data');
			return cached.data;
		}

		this.metrics.cacheMisses++;
		
		try {
			const products = await this.retryOperation(
				() => this.fetchProducts(),
				this.config.api.retries.maxRetries
			);
			
			this.runtime.cache.set(cacheKey, { 
				data: products, 
				timestamp: Date.now() 
			});
			
			return products;

		} catch (error) {
			if (cached) {
				this.log('warn', 'Using stale cache due to fetch error');
				return cached.data;
			}
			throw error;
		}
	}

	async fetchProducts() {
		this.metrics.apiCalls++;
		
		const controller = new AbortController();
		const timeoutId = setTimeout(() => controller.abort(), this.config.api.retries.timeoutMs);

		try {
			const response = await fetch(this.config.api.endpoints.products, {
				signal: controller.signal,
				headers: {
					'Accept': 'application/json',
					'Cache-Control': 'no-cache'
				}
			});

			clearTimeout(timeoutId);

			if (!response.ok) {
				throw new Error(`HTTP ${response.status}: ${response.statusText}`);
			}

			const data = await response.json();

			if (!data || !Array.isArray(data.products)) {
				throw new Error('Invalid product data structure');
			}

			this.log('info', `Fetched ${data.products.length} products successfully`);
			return data;

		} catch (error) {
			clearTimeout(timeoutId);
			
			if (error.name === 'AbortError') {
				throw new Error('Request timeout');
			}
			
			throw error;
		}
	}

	updatePrice() {
		try {
			const priceSelectors = this.config.ui.selectors.priceElements;
			let priceElement = null;

			for (const selector of priceSelectors) {
				priceElement = document.querySelector(selector);
				if (priceElement) break;
			}

			if (!priceElement) {
				const elements = document.querySelectorAll('*');
				for (const el of elements) {
					const text = el.textContent?.trim();
					if (text && text.includes(this.config.packs.currency) && el.children.length === 0) {
						priceElement = el;
						break;
					}
				}
			}

			if (!priceElement) {
				this.log('warn', 'Price element not found');
				return false;
			}

			const newPrice = this.config.packs.pricing[this.runtime.selectedPackSize];
			if (!newPrice) {
				this.log('error', `No price mapping for pack size: ${this.runtime.selectedPackSize}`);
				return false;
			}

			const formattedPrice = `${this.config.packs.currency} ${Number(newPrice).toFixed(2)}`;
			priceElement.textContent = formattedPrice;

			if (priceElement.hasAttribute('data-price')) {
				priceElement.setAttribute('data-price', newPrice.toString());
			}

			if (this.config.ui.animations.enabled) {
				this.animatePriceChange(priceElement);
			}

			this.log('info', `Price updated to ${formattedPrice} for pack size ${this.runtime.selectedPackSize}`);
			return true;

		} catch (error) {
			this.handleError('updatePrice', error);
			return false;
		}
	}

	animatePriceChange(element) {
		element.style.transition = `all ${this.config.ui.animations.duration}ms ease`;
		element.style.transform = 'scale(1.05)';
		element.style.color = '#10b981';

		setTimeout(() => {
			element.style.transform = 'scale(1)';
			element.style.color = '';
		}, this.config.ui.animations.duration);
	}

	createPackSizeTabSelector() {
		const tabContainer = this.createElement('div', {
			className: 'pack-size-tabs'
		});

		tabContainer.style.cssText = `
			display: flex;
			justify-content: center;
			gap: 6px;
			margin-bottom: 32px;
			background: #f1f5f9;
			padding: 6px;
			border-radius: 12px;
			border: 1px solid #e2e8f0;
			max-width: 500px;
			margin-left: auto;
			margin-right: auto;
		`;

		this.config.packs.availableSizes.forEach(size => {
			const tab = this.createElement('span', {
				textContent: size.toString(),
				className: 'pack-size-tab'
			});

			// Store pack size using a simple property instead of data attribute
			tab._packSize = size;
			
			const isSelected = size === this.runtime.selectedPackSize;
			this.stylePackSizeTab(tab, isSelected);

			tab.addEventListener('mouseenter', () => this.handleTabHover(tab, true));
			tab.addEventListener('mouseleave', () => this.handleTabHover(tab, false));
			tab.addEventListener('click', () => this.handlePackSizeChange(size));

			tabContainer.appendChild(tab);
		});

		return tabContainer;
	}

	stylePackSizeTab(tab, isSelected) {
		const baseStyles = `
			padding: 12px 20px;
			border: none;
			border-radius: 8px;
			cursor: pointer;
			font-weight: 600;
			font-size: 14px;
			transition: all 0.2s ease-in-out;
			min-width: 70px;
			text-align: center;
			user-select: none;
		`;

		if (isSelected) {
			tab.classList.add('selected');
			tab.style.cssText = baseStyles + `
				background: #3b82f6;
				color: white;
				box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);
				transform: translateY(-1px);
			`;
		} else {
			tab.classList.remove('selected');
			tab.style.cssText = baseStyles + `
				background: transparent;
				color: #64748b;
				box-shadow: none;
				transform: none;
			`;
		}
	}

	handleTabHover(tab, isHovering) {
		if (tab.classList.contains('selected')) return;

		if (isHovering) {
			tab.style.backgroundColor = '#e2e8f0';
			tab.style.color = '#334155';
			tab.style.transform = 'translateY(-1px)';
		} else {
			tab.style.backgroundColor = 'transparent';
			tab.style.color = '#64748b';
			tab.style.transform = 'none';
		}
	}

	handlePackSizeChange(newPackSize) {
		if (newPackSize === this.runtime.selectedPackSize) return;

		const oldPackSize = this.runtime.selectedPackSize;
		this.runtime.selectedPackSize = newPackSize;

		document.querySelectorAll('.pack-size-tab').forEach(tab => {
			const tabSize = tab._packSize; // Use stored property instead of data attribute
			this.stylePackSizeTab(tab, tabSize === newPackSize);
		});

		this.updateSelectorsForNewPackSize(oldPackSize, newPackSize);

		if (this.runtime.currentPackConfig) {
			this.runtime.currentPackConfig.packCount = newPackSize;
		}

		this.initializeSelectorsState(newPackSize);
		this.schedulePriceUpdate(100);

		this.log('info', `Pack size changed from ${oldPackSize} to ${newPackSize}`);
	}

	updateSelectorsForNewPackSize(oldSize, newSize) {
		const selectorsContainer = document.querySelector('.pack-selectors-container');
		if (!selectorsContainer) return;

		try {
			const uniqueOptions = this.extractUniqueOptions(this.state.products);

			this.cleanupSelectors();
			selectorsContainer.innerHTML = '';

			for (let i = 0; i < newSize; i++) {
				const selectorSet = this.createSelectorSet(i + 1, uniqueOptions);
				selectorsContainer.appendChild(selectorSet.element);
				this.runtime.selectors.push(selectorSet);
			}

			const progressIndicator = document.querySelector('.pack-progress-indicator');
			if (progressIndicator) {
				progressIndicator.textContent = `Selected 0 of ${newSize} items`;
			}

			setTimeout(() => this.setupSelectorListeners(), 100);

		} catch (error) {
			this.handleError('updateSelectorsForNewPackSize', error);
		}
	}

	cleanupSelectors() {
		this.runtime.selectors.forEach(selector => {
			if (selector.cleanup && typeof selector.cleanup === 'function') {
				try {
					selector.cleanup();
				} catch (error) {
					this.log('warn', 'Error cleaning up selector:', error);
				}
			}
		});
		this.runtime.selectors = [];
	}

	createSelectorSet(itemNumber, uniqueOptions) {
		const wrapper = this.createElement('div', {
			className: 'pack-item-selector'
		});
		
		// Store item number using property instead of data attribute
		wrapper._itemNumber = itemNumber;
		
		wrapper.style.cssText = `
			display: flex;
			flex-direction: column;
			gap: 12px;
			align-items: start;
			padding: 12px;
			background: white;
			border-radius: 6px;
			box-shadow: 0 1px 3px rgba(0,0,0,0.1);
		`;

		const label = this.createElement('div', {
			textContent: `Item ${itemNumber}:`,
			style: 'min-width: 80px; font-weight: 600; color: #374151; font-size: 16px;'
		});

		wrapper.appendChild(label);

		const sizeContainer = this.createSizeSelector(itemNumber, uniqueOptions.sizes);
		wrapper.appendChild(sizeContainer);

		const colorWrapper = this.createColorSelector(itemNumber, uniqueOptions.colors);
		wrapper.appendChild(colorWrapper);

		const availability = this.createElement('div', {
			className: 'pack-item-availability',
			style: 'min-width: 100px; text-align: right; font-size: 12px; color: #f97316; font-weight: 500;'
		});
		wrapper.appendChild(availability);

		return {
			element: wrapper,
			sizeSelect: sizeContainer,
			colorSelect: colorWrapper,
			availability,
			itemNumber,
			cleanup: () => {
				if (colorWrapper._cleanup) {
					colorWrapper._cleanup();
				}
			}
		};
	}

	createSizeSelector(itemNumber, sizes) {
		const sizeContainer = this.createElement('div', {
			className: 'pack-size-pill-group'
		});
		
		sizeContainer._itemNumber = itemNumber; // Use property instead of data attribute
		sizeContainer.style.cssText = 'display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px;';

		sizes.forEach(size => {
			const pillLabel = this.createElement('label', {
				className: 'pack-size-pill'
			});
			
			pillLabel.style.cssText = `
				display: inline-flex;
				align-items: center;
				justify-content: center;
				padding: 8px 12px;
				border: 2px solid #e5e7eb;
				border-radius: 20px;
				cursor: pointer;
				font-size: 14px;
				font-weight: 500;
				transition: all 0.2s ease;
				background: white;
				user-select: none;
			`;

			const input = this.createElement('input', {
				type: 'radio',
				name: `size-${itemNumber}`,
				value: size,
				style: 'display: none;'
			});

			const span = this.createElement('span', {
				textContent: size
			});

			const handleHover = (isEntering) => {
				if (!input.checked) {
					pillLabel.style.borderColor = isEntering ? '#3b82f6' : '#e5e7eb';
					pillLabel.style.backgroundColor = isEntering ? '#eff6ff' : 'white';
				}
			};

			const handleSelection = () => {
				const allPills = sizeContainer.querySelectorAll('.pack-size-pill');
				allPills.forEach(pill => {
					pill.style.borderColor = '#e5e7eb';
					pill.style.backgroundColor = 'white';
					pill.style.color = '#374151';
				});

				if (input.checked) {
					pillLabel.style.borderColor = '#3b82f6';
					pillLabel.style.backgroundColor = '#3b82f6';
					pillLabel.style.color = 'white';
				}
			};

			pillLabel.addEventListener('mouseenter', () => handleHover(true));
			pillLabel.addEventListener('mouseleave', () => handleHover(false));
			input.addEventListener('change', handleSelection);

			pillLabel.appendChild(input);
			pillLabel.appendChild(span);
			sizeContainer.appendChild(pillLabel);
		});

		return sizeContainer;
	}

	createColorSelector(itemNumber, colors) {
		const colorWrapper = this.createElement('div', {
			className: 'pack-color-dropdown'
		});
		
		colorWrapper._itemNumber = itemNumber; // Use property instead of data attribute
		colorWrapper.style.cssText = `
			position: relative;
			width: 100%;
			border: 2px solid #e5e7eb;
			border-radius: 8px;
			background: white;
			z-index: 10;
		`;

		const selectedColor = this.createElement('div', {
			className: 'pack-color-selected',
			textContent: 'Pick Color'
		});
		
		selectedColor.style.cssText = `
			padding: 12px;
			cursor: pointer;
			display: flex;
			align-items: center;
			justify-content: space-between;
			font-size: 14px;
			color: #374151;
			background: white;
			transition: background-color 0.3s ease;
			user-select: none;
			-webkit-tap-highlight-color: transparent;
		`;

		const arrow = this.createElement('span', {
			textContent: '▼',
			style: 'font-size: 10px; color: #6b7280; transition: transform 0.2s ease; pointer-events: none;'
		});
		selectedColor.appendChild(arrow);

		const optionsContainer = this.createElement('div', {
			className: 'pack-color-options'
		});
		
		optionsContainer.style.cssText = `
			position: absolute;
			top: 100%;
			left: 0;
			right: 0;
			background: white;
			border: 2px solid #e5e7eb;
			border-top: none;
			border-radius: 0 0 8px 8px;
			max-height: 200px;
			overflow-y: auto;
			z-index: 1000;
			display: none;
			box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
			-webkit-overflow-scrolling: touch;
			touch-action: pan-y;
		`;

		let isDropdownOpen = false;
		let isScrolling = false;
		let scrollTimeout = null;

		const trackScrolling = () => {
			isScrolling = true;
			clearTimeout(scrollTimeout);
			scrollTimeout = setTimeout(() => {
				isScrolling = false;
			}, 150);
		};

		optionsContainer.addEventListener('scroll', trackScrolling, { passive: true });

		colors.forEach(color => {
			const option = this.createElement('div', {
				className: 'pack-color-option'
			});
			
			option.style.cssText = `
				display: flex;
				align-items: center;
				gap: 8px;
				padding: 12px 16px;
				cursor: pointer;
				transition: background-color 0.2s ease;
				user-select: none;
				-webkit-tap-highlight-color: transparent;
			`;

			const img = this.createElement('img', {
				src: this.getImageForColor(color),
				alt: color
			});
			
			img.style.cssText = `
				width: 24px;
				height: 24px;
				object-fit: cover;
				border-radius: 4px;
				border: 1px solid #e5e7eb;
				pointer-events: none;
			`;

			img.onerror = () => {
				img.src = this.generatePlaceholderImage();
			};

			const name = this.createElement('span', {
				textContent: color,
				style: 'font-size: 14px; color: #1f2937; flex: 1; pointer-events: none;'
			});

			option.appendChild(img);
			option.appendChild(name);

			let touchStartY = 0;
			let hasMoved = false;

			const handleTouchStart = (e) => {
				touchStartY = e.touches[0].clientY;
				hasMoved = false;
				isScrolling = false;
			};

			const handleTouchMove = (e) => {
				if (!touchStartY) return;
				const touchY = e.touches[0].clientY;
				const deltaY = Math.abs(touchY - touchStartY);

				if (deltaY > 10) {
					hasMoved = true;
					isScrolling = true;
				}
			};

			const handleTouchEnd = (e) => {
				if (hasMoved || isScrolling) {
					touchStartY = 0;
					hasMoved = false;
					return;
				}
				setTimeout(() => {
					if (!isScrolling) {
						handleOptionSelect(e);
					}
				}, 50);
			};

			const handleOptionSelect = (e) => {
				e.preventDefault();
				e.stopPropagation();

				if (isScrolling) {
					this.log('debug', 'Preventing selection during scroll');
					return;
				}

				optionsContainer.querySelectorAll('.pack-color-option').forEach(opt => {
					opt.classList.remove('selected');
					opt.style.backgroundColor = 'transparent';
				});

				selectedColor.innerHTML = '';
				selectedColor.textContent = color;
				selectedColor.appendChild(arrow);
				selectedColor._value = color; // Use property instead of data attribute

				option.classList.add('selected');

				optionsContainer.style.display = 'none';
				arrow.style.transform = 'rotate(0deg)';
				isDropdownOpen = false;

				colorWrapper.style.borderColor = '#10b981';

				setTimeout(() => {
					this.handleSelectorChange({ target: colorWrapper });
				}, 50);
			};

			option.addEventListener('mouseenter', () => {
				if (!option.classList.contains('selected') && !isScrolling) {
					option.style.backgroundColor = '#f3f4f6';
				}
			});

			option.addEventListener('mouseleave', () => {
				if (!option.classList.contains('selected')) {
					option.style.backgroundColor = 'transparent';
				}
			});

			option.addEventListener('click', (e) => {
				if (!('ontouchstart' in window) || !hasMoved) {
					handleOptionSelect(e);
				}
			});

			option.addEventListener('touchstart', handleTouchStart, { passive: true });
			option.addEventListener('touchmove', handleTouchMove, { passive: true });
			option.addEventListener('touchend', handleTouchEnd, { passive: false });

			optionsContainer.appendChild(option);
		});

		const toggleDropdown = (e) => {
			e.preventDefault();
			e.stopPropagation();

			document.querySelectorAll('.pack-color-options').forEach(dropdown => {
				if (dropdown !== optionsContainer) {
					dropdown.style.display = 'none';
					const otherArrow = dropdown.parentElement.querySelector('span');
					if (otherArrow && otherArrow !== arrow) {
						otherArrow.style.transform = 'rotate(0deg)';
					}
				}
			});

			if (isDropdownOpen) {
				optionsContainer.style.display = 'none';
				arrow.style.transform = 'rotate(0deg)';
				isDropdownOpen = false;
			} else {
				optionsContainer.style.display = 'block';
				arrow.style.transform = 'rotate(180deg)';
				isDropdownOpen = true;
				optionsContainer.scrollTop = 0;
			}
		};

		selectedColor.addEventListener('click', toggleDropdown);

		const handleOutsideClick = (e) => {
			if (!colorWrapper.contains(e.target) && isDropdownOpen) {
				optionsContainer.style.display = 'none';
				arrow.style.transform = 'rotate(0deg)';
				isDropdownOpen = false;
			}
		};

		document.addEventListener('click', handleOutsideClick, true);

		colorWrapper._cleanup = () => {
			document.removeEventListener('click', handleOutsideClick, true);
			if (scrollTimeout) {
				clearTimeout(scrollTimeout);
			}
		};

		colorWrapper.appendChild(selectedColor);
		colorWrapper.appendChild(optionsContainer);

		return colorWrapper;
	}

	getImageForColor(color) {
		return this.runtime.colorImages[color] || this.generatePlaceholderImage();
	}

	generatePlaceholderImage() {
		return "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMiA4VjE2TTggMTJIMTYiIHN0cm9rZT0iIzlDQTNBRiIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiLz4KPC9zdmc+";
	}

	createBundleInterface(parent, products, _packConfig) {
		try {
			const existing = document.querySelector(`.${PackBundleCreator.NAMESPACE}`);
			if (existing) existing.remove();

			const uniqueOptions = this.extractUniqueOptions(products);

			const wrapper = this.createElement('div', {
				className: PackBundleCreator.NAMESPACE
			});
			
			wrapper.style.cssText = `
				padding: 20px;
				border: 2px solid #e2e8f0;
				border-radius: 8px;
				background: #f8fafc;
				margin: 10px 0;
			`;

			const header = this.createElement('div', {
				textContent: '🎁 Build Your Custom Pack Bundle',
				style: 'font-weight: bold; font-size: 18px; margin-bottom: 15px; text-align: center; color: #1e293b;'
			});

			const description = this.createElement('div', {
				textContent: 'Pick Any',
				style: 'font-size: 14px; color: #000; text-align: start; margin-bottom: 20px; font-weight: 600;'
			});

			const packSizeTabSelector = this.createPackSizeTabSelector();

			const selectorsContainer = this.createElement('div', {
				className: 'pack-selectors-container'
			});
			
			selectorsContainer.style.cssText = `
				display: flex;
				flex-direction: column;
				gap: 12px;
				max-width: 600px;
				margin: 0 auto;
			`;

			this.runtime.selectors = [];
			for (let i = 0; i < this.runtime.selectedPackSize; i++) {
				const selectorSet = this.createSelectorSet(i + 1, uniqueOptions);
				selectorsContainer.appendChild(selectorSet.element);
				this.runtime.selectors.push(selectorSet);
			}

			const progress = this.createProgressIndicator(this.runtime.selectedPackSize);
			const validationArea = this.createElement('div', {
				className: 'pack-validation-messages',
				style: 'margin-top: 15px; min-height: 24px;'
			});

			wrapper.appendChild(header);
			wrapper.appendChild(description);
			wrapper.appendChild(packSizeTabSelector);
			wrapper.appendChild(selectorsContainer);
			wrapper.appendChild(progress);
			wrapper.appendChild(validationArea);

			parent.insertAdjacentElement('afterend', wrapper);
			this.hideLoadingState();

			setTimeout(() => this.setupSelectorListeners(), 100);

		} catch (error) {
			this.handleError('createBundleInterface', error, {
				showUser: true,
				fallback: () => this.restoreDefaultBehavior()
			});
		}
	}

	createProgressIndicator(packCount) {
		const progress = this.createElement('div', {
			className: 'pack-progress-indicator'
		});
		
		progress.style.cssText = `
			text-align: center;
			margin-top: 15px;
			padding: 10px;
			background: #eff6ff;
			border: 1px solid #3b82f6;
			border-radius: 6px;
			font-weight: 600;
			color: #1e40af;
		`;
		
		progress.textContent = `Selected 0 of ${packCount} items`;
		return progress;
	}

	setupSelectorListeners() {
		try {
			this.runtime.selectors.forEach(selector => {
				const sizeRadios = selector.sizeSelect?.querySelectorAll('input[type=radio]');
				sizeRadios?.forEach(radio => {
					radio.addEventListener('change', this.handleSelectorChange);
				});
			});
		} catch (error) {
			this.handleError('setupSelectorListeners', error);
		}
	}

	handleSelectorChange(e) {
		try {
			const wrapper = e.target.closest('.pack-item-selector');
			if (!wrapper) return;

			const itemNumber = wrapper._itemNumber; // Use property instead of dataset

			const sizeInput = wrapper.querySelector(`input[name="size-${itemNumber}"]:checked`);
			const size = sizeInput ? sizeInput.value : '';

			const colorWrapper = wrapper.querySelector('.pack-color-selected');
			const color = colorWrapper?._value || ''; // Use property instead of dataset

			const variant = this.findVariant(size, color);

			this.state.selectedItems.set(itemNumber, {
				size,
				color,
				variantId: variant ? variant.id : null,
				isValid: !!variant,
				product: variant ? this.findProductByVariant(variant.id) : null
			});

			this.updateUI();

		} catch (error) {
			this.handleError('handleSelectorChange', error);
		}
	}

	findVariant(size, color) {
		if (!size || !color) return null;

		try {
			return this.state.products
				.flatMap(product => product.variants || [])
				.find(variant => 
					variant.option1 === size && 
					variant.option2 === color && 
					variant.available
				);
		} catch (error) {
			this.handleError('findVariant', error);
			return null;
		}
	}

	findProductByVariant(variantId) {
		try {
			return this.state.products.find(product => 
				product.variants?.some(variant => variant.id === variantId)
			);
		} catch (error) {
			this.handleError('findProductByVariant', error);
			return null;
		}
	}

	resetState() {
		this.state = {
			isLoading: false,
			selectedItems: new Map(),
			products: [],
			validationErrors: [],
			validationWarnings: [],
			initialized: this.state.initialized
		};
		
		this.cleanupSelectors();
		this.runtime.colorImages = {};
	}

	setState(newState) {
		this.state = { ...this.state, ...newState };
		this.updateUI();
	}

	initializeSelectorsState(packCount) {
		this.state.selectedItems.clear();
		for (let i = 1; i <= packCount; i++) {
			this.state.selectedItems.set(i, {
				size: '',
				color: '',
				variantId: null,
				isValid: false,
				product: null
			});
		}
		this.updateUI();
	}

	updateUI() {
		try {
			this.updateProgress();
			this.updateButtonStates();
			this.updateValidationMessages();
			this.updateAvailabilityIndicators();
		} catch (error) {
			this.handleError('updateUI', error);
		}
	}

	updateProgress() {
		const progress = document.querySelector('.pack-progress-indicator');
		if (!progress) return;

		const total = this.runtime.selectedPackSize;
		const selected = Array.from(this.state.selectedItems.values())
			.filter(item => item.isValid).length;

		progress.textContent = `Selected ${selected} of ${total} items`;

		if (selected === total && total > 0) {
			this.enableButtons();
		}
	}

	updateButtonStates() {
		const isValid = this.validateAllSelected();
		const isLoading = this.state.isLoading;

		const addBtn = document.querySelector(this.config.ui.selectors.addToCartBtn);
		const buyBtn = document.querySelector(this.config.ui.selectors.buyNowBtn);

		if (addBtn) addBtn.disabled = !isValid || isLoading;
		if (buyBtn) buyBtn.disabled = !isValid || isLoading;
	}

	updateAvailabilityIndicators() {
		this.runtime.selectors.forEach(selector => {
			const itemState = this.state.selectedItems.get(selector.itemNumber);
			
			if (!itemState?.size || !itemState?.color) {
				selector.availability.textContent = '';
				selector.availability.style.color = '#6b7280';
			} else if (!itemState.isValid) {
				selector.availability.textContent = '❌ Unavailable';
				selector.availability.style.color = '#ef4444';
			} else {
				selector.availability.textContent = '✅ Available';
				selector.availability.style.color = '#10b981';
			}
		});
	}

	updateValidationMessages() {
		const validationArea = document.querySelector('.pack-validation-messages');
		if (!validationArea) return;

		const selectedCount = Array.from(this.state.selectedItems.values())
			.filter(item => item.isValid).length;
		const totalCount = this.runtime.selectedPackSize;
		const remaining = totalCount - selectedCount;

		if (remaining > 0) {
			validationArea.innerHTML = `
				<div style="padding: 8px 12px; background: #fef3c7; border: 1px solid #f59e0b; border-radius: 6px; font-size: 14px; color: #92400e;">
					⚠️ Please select ${remaining} more item${remaining > 1 ? 's' : ''} to complete your ${totalCount}-pack bundle
				</div>
			`;
		} else if (selectedCount === totalCount && totalCount > 0) {
			validationArea.innerHTML = `
				<div style="padding: 8px 12px; background: #d1fae5; border: 1px solid #10b981; border-radius: 6px; font-size: 14px; color: #065f46;">
					🎉 ${totalCount}-pack bundle complete! Ready to add to cart
				</div>
			`;
		} else {
			validationArea.innerHTML = '';
		}
	}

	showLoadingState(parent) {
		if (document.querySelector('.pack-loader')) return;

		const loader = this.createElement('div', {
			className: 'pack-loader'
		});

		loader.innerHTML = `
			<div style="display: flex; align-items: center; justify-content: center; gap: 8px; padding: 20px;">
				<div style="width: 20px; height: 20px; border: 2px solid #3b82f6; border-top: transparent; border-radius: 50%; animation: pack-spin 0.8s linear infinite;"></div>
				<span style="font-weight: 600; color: #1e40af;">Loading pack options...</span>
			</div>
		`;

		loader.style.cssText = 'text-align: center; margin: 10px 0;';

		if (!document.querySelector('#pack-spin-keyframes')) {
			const style = this.createElement('style', {
				id: 'pack-spin-keyframes',
				textContent: '@keyframes pack-spin { to { transform: rotate(360deg); } }'
			});
			document.head.appendChild(style);
		}

		parent.insertAdjacentElement('afterend', loader);
	}

	hideLoadingState() {
		const loader = document.querySelector('.pack-loader');
		if (loader) loader.remove();
	}

	removeLoadingState() {
		this.hideLoadingState();
	}

	enableButtons() {
		const addBtn = document.querySelector(this.config.ui.selectors.addToCartBtn);
		const buyBtn = document.querySelector(this.config.ui.selectors.buyNowBtn);
		
		if (addBtn) addBtn.disabled = false;
		if (buyBtn) buyBtn.disabled = false;
	}

	setupButtonOverrides() {
		try {
			const addBtn = document.querySelector(this.config.ui.selectors.addToCartBtn);
			const buyBtn = document.querySelector(this.config.ui.selectors.buyNowBtn);

			if (addBtn) {
				addBtn.type = 'button';
				addBtn.addEventListener('click', this.handleAddToCart);
				addBtn.disabled = true;
			}

			if (buyBtn) {
				buyBtn.style.display = 'none';
			}

		} catch (error) {
			this.handleError('setupButtonOverrides', error);
		}
	}

	handleAddToCart(e) {
		e.preventDefault();

		if (!this.validateAllSelected()) {
			this.showUserError('Please complete your bundle selections before adding to cart.');
			return;
		}

		this.performAddToCart(e.target);
	}

	async performAddToCart(button) {
		const originalText = button.textContent;
		const spinner = this.createSpinner();

		try {
			button.disabled = true;
			button.textContent = 'Adding to Cart';
			button.appendChild(spinner);

			const formData = {
				items: Array.from(this.state.selectedItems.values())
					.filter(item => item.isValid)
					.map(item => ({
						id: item.variantId,
						quantity: 1
					}))
			};

			const response = await fetch(this.config.api.endpoints.addToCart, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(formData)
			});

			if (!response.ok) {
				throw new Error(`Failed to add to cart: ${response.status}`);
			}

			await response.json();

			button.disabled = false;
			button.textContent = originalText;
			spinner.remove();

			const cartTrigger = document.querySelector("[aria-controls='cart']");
			if (cartTrigger) {
				cartTrigger.click();
			} else {
				this.showSuccessMessage(`${this.runtime.selectedPackSize}-pack bundle added to cart successfully!`);
			}

		} catch (error) {
			button.disabled = false;
			button.textContent = originalText;
			spinner.remove();

			this.handleError('performAddToCart', error, {
				showUser: true,
				userMessage: 'Error adding bundle to cart. Please try again.'
			});
		}
	}

	createSpinner() {
		const spinner = this.createElement('span', {
			className: 'pack-btn-spinner'
		});
		
		spinner.style.cssText = `
			display: inline-block;
			width: 16px;
			height: 16px;
			margin-left: 8px;
			border: 2px solid #fff;
			border-top-color: transparent;
			border-radius: 50%;
			animation: pack-spin 0.6s linear infinite;
		`;
		
		return spinner;
	}

	showSuccessMessage(message) {
		const existing = document.querySelector('.pack-success-message');
		if (existing) existing.remove();

		const successMsg = this.createElement('div', {
			className: 'pack-success-message',
			textContent: message
		});

		successMsg.style.cssText = `
			position: fixed;
			top: 20px;
			right: 20px;
			background: #10b981;
			color: white;
			padding: 12px 20px;
			border-radius: 8px;
			font-weight: 600;
			z-index: 9999;
			box-shadow: 0 4px 12px rgba(0,0,0,0.15);
			animation: pack-slideIn 0.3s ease-out;
		`;

		if (!document.querySelector('#pack-slideIn-keyframes')) {
			const style = this.createElement('style', {
				id: 'pack-slideIn-keyframes',
				textContent: `
					@keyframes pack-slideIn {
						from { transform: translateX(100%); opacity: 0; }
						to { transform: translateX(0); opacity: 1; }
					}
				`
			});
			document.head.appendChild(style);
		}

		document.body.appendChild(successMsg);

		setTimeout(() => {
			successMsg.style.animation = 'pack-slideIn 0.3s ease-out reverse';
			setTimeout(() => successMsg.remove(), 300);
		}, 3000);
	}

	validateAllSelected() {
		const requiredCount = this.runtime.selectedPackSize;
		const validItems = Array.from(this.state.selectedItems.values())
			.filter(item => item.isValid);
		return validItems.length === requiredCount;
	}

	retryOperation(operation, maxRetries) {
		const attempt = (retriesLeft) => {
			return operation().catch(error => {
				if (retriesLeft > 0) {
					this.log('warn', `Operation failed, retrying... (${retriesLeft} attempts left)`);
					return new Promise(resolve => 
						setTimeout(resolve, this.config.api.retries.retryDelay)
					).then(() => attempt(retriesLeft - 1));
				}
				throw error;
			});
		};

		return attempt(maxRetries);
	}

	restoreDefaultBehavior() {
		try {
			const wrapper = document.querySelector(`.${PackBundleCreator.NAMESPACE}`);
			if (wrapper) wrapper.remove();

			this.cleanupSelectors();

			const addBtn = document.querySelector(this.config.ui.selectors.addToCartBtn);
			const buyBtn = document.querySelector(this.config.ui.selectors.buyNowBtn);

			if (addBtn) {
				addBtn.disabled = false;
				addBtn.type = 'submit';
				const newAddBtn = addBtn.cloneNode(true);
				addBtn.parentNode.replaceChild(newAddBtn, addBtn);
			}

			if (buyBtn) {
				buyBtn.style.display = '';
				buyBtn.disabled = false;
			}

			const originalSelector = document.querySelector(this.config.ui.selectors.variantSelection);
			if (originalSelector) {
				originalSelector.classList.remove('hidden');
				this.runtime.initializedElements.delete(originalSelector);
			}

			this.log('info', 'Default behavior restored');

		} catch (error) {
			this.handleError('restoreDefaultBehavior', error);
		}
	}

	cleanup() {
		try {
			this.log('info', 'Cleaning up Pack Bundle Creator...');

			this.restoreDefaultBehavior();
			this.hideLoadingState();
			this.resetState();

			this.runtime.currentPackConfig = null;
			this.runtime.selectedPackSize = null;

			if (this.runtime.priceUpdateTimer) {
				clearTimeout(this.runtime.priceUpdateTimer);
				this.runtime.priceUpdateTimer = null;
			}

			this.runtime.observers.forEach(observerData => {
				if (observerData.cleanup) {
					observerData.cleanup();
				}
				if (observerData.observer) {
					observerData.observer.disconnect();
				}
				if (observerData.timeoutId) {
					clearTimeout(observerData.timeoutId);
				}
				if (observerData.urlCheckInterval) {
					clearInterval(observerData.urlCheckInterval);
				}
			});
			this.runtime.observers.clear();

			const successMsg = document.querySelector('.pack-success-message');
			if (successMsg) successMsg.remove();

			const errorMsg = document.querySelector(`.${PackBundleCreator.NAMESPACE}-error`);
			if (errorMsg) errorMsg.remove();

			this.runtime.cache.clear();

			this.log('info', 'Cleanup completed');

		} catch (error) {
			this.log('error', 'Error during cleanup:', error);
		}
	}

	createElement(tag, props = {}) {
		const element = document.createElement(tag);
		
		Object.entries(props).forEach(([key, value]) => {
			if (key === 'style') {
				element.style.cssText = value;
			} else if (key === 'className') {
				element.className = value;
			} else if (key === 'textContent') {
				element.textContent = value;
			} else if (key === 'innerHTML') {
				element.innerHTML = value;
			} else if (key in element) {
				element[key] = value;
			} else {
				element.setAttribute(key, value);
			}
		});

		return element;
	}

	debounce(func, delay) {
		let timeoutId;
		return (...args) => {
			clearTimeout(timeoutId);
			timeoutId = setTimeout(() => func.apply(this, args), delay);
		};
	}

	getMetrics() {
		return {
			...this.metrics,
			uptime: performance.now() - this.metrics.initTime,
			cacheSize: this.runtime.cache.size,
			selectorsCount: this.runtime.selectors.length,
			selectedItemsCount: this.state.selectedItems.size
		};
	}

	logMetrics() {
		this.log('info', 'Pack Bundle Creator Metrics:', this.getMetrics());
	}

	validateConfig() {
		const issues = [];

		this.config.packs.availableSizes.forEach(size => {
			if (!this.config.packs.pricing[size]) {
				issues.push(`Missing price mapping for pack size: ${size}`);
			}
		});

		if (!document.querySelector(this.config.ui.selectors.variantSelection)) {
			issues.push('Required variant selection element not found');
		}

		if (!this.config.api.endpoints.products.includes('/products.json')) {
			issues.push('Products API endpoint may be invalid');
		}

		if (issues.length > 0) {
			this.log('warn', 'Configuration validation issues:', issues);
			return false;
		}

		return true;
	}

	healthCheck() {
		const health = {
			status: 'healthy',
			timestamp: new Date().toISOString(),
			version: PackBundleCreator.VERSION,
			issues: []
		};

		try {
			if (!this.state.initialized) {
				health.issues.push('Not initialized');
			}

			if (this.runtime.currentPackConfig && !document.querySelector(`.${PackBundleCreator.NAMESPACE}`)) {
				health.issues.push('Bundle interface missing');
			}

			if (!this.validateConfig()) {
				health.issues.push('Configuration validation failed');
			}

			if (this.runtime.cache.size > 100) {
				health.issues.push('Cache size too large');
			}

			if (health.issues.length > 0) {
				health.status = 'degraded';
			}

		} catch (error) {
			health.status = 'unhealthy';
			health.issues.push(`Health check error: ${error.message}`);
		}

		return health;
	}

	static create(config = {}) {
		return new PackBundleCreator(config);
	}

	static getInstance(config = {}) {
		if (!window[PackBundleCreator.NAMESPACE]) {
			window[PackBundleCreator.NAMESPACE] = new PackBundleCreator(config);
		}
		return window[PackBundleCreator.NAMESPACE];
	}

	destroy() {
		this.cleanup();
		this.state.initialized = false;
		
		if (window[PackBundleCreator.NAMESPACE] === this) {
			delete window[PackBundleCreator.NAMESPACE];
		}

		this.log('info', 'Pack Bundle Creator instance destroyed');
	}
}

// Auto-initialization with error boundary
document.addEventListener('DOMContentLoaded', () => {
	try {
		const packBundleCreator = PackBundleCreator.getInstance({
			dev: {
				debug: false,
				logLevel: 'info'
			}
		});

		// Development utilities
		if (window.location.hostname === 'localhost' || window.location.hostname.includes('dev')) {
			window.PackBundleCreator = PackBundleCreator;
			window.packBundleCreator = packBundleCreator;
			
			window.packBundleMetrics = () => packBundleCreator.logMetrics();
			window.packBundleHealth = () => console.log(packBundleCreator.healthCheck());
		}

	} catch (error) {
		console.error('[pbc:ERROR] Failed to initialize:', error);
		
		if (typeof window.errorTracker !== 'undefined') {
			window.errorTracker.captureException(error);
		}
	}
});

// Handle case where script loads after DOM is ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => {
		if (!window[PackBundleCreator.NAMESPACE]) {
			PackBundleCreator.getInstance();
		}
	});
} else if (!window[PackBundleCreator.NAMESPACE]) {
	PackBundleCreator.getInstance();
}

// Handle page visibility for performance optimization
document.addEventListener('visibilitychange', () => {
	const instance = window[PackBundleCreator.NAMESPACE];
	if (instance && document.hidden) {
		clearTimeout(instance.runtime.priceUpdateTimer);
	}
});

// Cleanup on page unload
window.addEventListener('beforeunload', () => {
	const instance = window[PackBundleCreator.NAMESPACE];
	if (instance) {
		instance.cleanup();
	}
});